import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingHeroSection = ({ onCalculatorScroll, onContactScroll }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const translations = {
    en: {
      title: "Transparent Pricing for Every Manufacturing Scale",
      subtitle: "No hidden costs, no surprise fees. Choose the perfect ERP plan for your aluminum extrusion business with flexible payment options and guaranteed ROI.",
      keyPoints: [
        "30-day free trial with full features",
        "No setup fees or hidden charges",
        "Flexible monthly or annual billing",
        "24/7 support included in all plans",
        "Free data migration and training"
      ],
      buttons: {
        calculator: "Get Custom Quote",
        contact: "Talk to Sales Expert"
      },
      stats: {
        avgSavings: "Average Cost Savings",
        avgSavingsValue: "₹2.5L",
        avgSavingsDesc: "per month",
        roiPeriod: "ROI Achievement",
        roiPeriodValue: "6 months",
        roiPeriodDesc: "average payback",
        clients: "Happy Clients",
        clientsValue: "500+",
        clientsDesc: "across India"
      }
    },
    hi: {
      title: "हर निर्माण स्केल के लिए पारदर्शी मूल्य निर्धारण",
      subtitle: "कोई छुपी हुई लागत नहीं, कोई आश्चर्यजनक शुल्क नहीं। लचीले भुगतान विकल्पों और गारंटीशुदा ROI के साथ अपने एल्यूमीनियम एक्सट्रूज़न व्यवसाय के लिए सही ERP योजना चुनें।",
      keyPoints: [
        "पूर्ण सुविधाओं के साथ 30-दिन मुफ्त ट्रायल",
        "कोई सेटअप शुल्क या छुपे हुए शुल्क नहीं",
        "लचीला मासिक या वार्षिक बिलिंग",
        "सभी योजनाओं में 24/7 सहायता शामिल",
        "मुफ्त डेटा माइग्रेशन और प्रशिक्षण"
      ],
      buttons: {
        calculator: "कस्टम कोट प्राप्त करें",
        contact: "बिक्री विशेषज्ञ से बात करें"
      },
      stats: {
        avgSavings: "औसत लागत बचत",
        avgSavingsValue: "₹2.5L",
        avgSavingsDesc: "प्रति माह",
        roiPeriod: "ROI प्राप्ति",
        roiPeriodValue: "6 महीने",
        roiPeriodDesc: "औसत पेबैक",
        clients: "खुश क्लाइंट्स",
        clientsValue: "500+",
        clientsDesc: "भारत भर में"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  return (
    <section className="relative py-20 bg-gradient-to-br from-primary-50 to-accent-50 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-20 h-20 bg-primary rounded-full"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-accent rounded-full"></div>
        <div className="absolute bottom-20 left-32 w-24 h-24 bg-primary rounded-full"></div>
        <div className="absolute bottom-32 right-10 w-12 h-12 bg-accent rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 shadow-sm">
                <Icon name="Tag" size={16} color="var(--color-primary)" />
                <span className="text-sm font-medium text-primary">
                  {currentLanguage === 'en' ? 'Transparent Pricing' : 'पारदर्शी मूल्य निर्धारण'}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                {t.title}
              </h1>
              
              <p className="text-lg text-text-secondary leading-relaxed">
                {t.subtitle}
              </p>
            </div>

            {/* Key Points */}
            <div className="space-y-4">
              {t.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-0.5">
                    <Icon name="Check" size={14} color="white" />
                  </div>
                  <span className="text-gray-700 font-medium">{point}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                onClick={onCalculatorScroll}
                className="bg-gradient-accent hover:bg-accent-600 shadow-cta hover-scale"
                iconName="Calculator"
                iconPosition="left"
              >
                {t.buttons.calculator}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={onContactScroll}
                className="border-primary text-primary hover:bg-primary hover:text-white"
                iconName="MessageCircle"
                iconPosition="left"
              >
                {t.buttons.contact}
              </Button>
            </div>
          </div>

          {/* Right Content - Stats */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Average Savings Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="TrendingUp" size={24} color="var(--color-success)" />
                  </div>
                  <div className="text-3xl font-bold text-success mb-2">
                    {t.stats.avgSavingsValue}
                  </div>
                  <div className="text-sm text-text-secondary mb-1">
                    {t.stats.avgSavingsDesc}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {t.stats.avgSavings}
                  </div>
                </div>
              </div>

              {/* ROI Period Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Clock" size={24} color="var(--color-primary)" />
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {t.stats.roiPeriodValue}
                  </div>
                  <div className="text-sm text-text-secondary mb-1">
                    {t.stats.roiPeriodDesc}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {t.stats.roiPeriod}
                  </div>
                </div>
              </div>

              {/* Clients Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300">
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={24} color="var(--color-accent)" />
                  </div>
                  <div className="text-3xl font-bold text-accent mb-2">
                    {t.stats.clientsValue}
                  </div>
                  <div className="text-sm text-text-secondary mb-1">
                    {t.stats.clientsDesc}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {t.stats.clients}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingHeroSection;